/**
 * Clientseitige Session-Policy: „Angemeldet bleiben" vs. „nur diese Sitzung".
 *
 * Supabase-Sessions sind project-weit über den Refresh-Token gültig. Wir
 * reichern das pro Gerät um ein INAKTIVITÄTS-Limit an – und zwar
 * MULTI-TAB-SICHER über localStorage (nicht sessionStorage).
 *
 *   • „Angemeldet bleiben" AN  → langes Inaktivitäts-Fenster (30 Tage).
 *   • „Angemeldet bleiben" AUS → kurzes Inaktivitäts-Fenster (8 Stunden).
 *
 * Jede Navigation im geschützten Bereich frischt den Aktivitäts-Zeitstempel
 * auf (siehe SessionGuard → touchActivity()). Bleibt das Gerät länger als
 * das Fenster inaktiv, meldet der Guard ab.
 *
 * Frühere Version nutzte sessionStorage, um „beim Browser-Schließen
 * ausloggen" abzubilden. Das hatte einen Bug: sessionStorage ist PRO TAB –
 * ein zweiter Tab hatte den Marker nicht und wurde sofort abgemeldet (und
 * riss per globalem signOut() alle Tabs mit). localStorage wird über alle
 * Tabs geteilt und behebt das.
 */

const REMEMBER_KEY = "dmd_remember";
const LAST_ACTIVITY_KEY = "dmd_last_activity";
/** Alt-Key der Vorgänger-Implementierung – wird beim Aufräumen entfernt. */
const LEGACY_SESSION_KEY = "dmd_session_started_at";

/** Inaktivitäts-Fenster ohne „Angemeldet bleiben" – 8 Stunden. */
export const SHORT_INACTIVITY_MS = 8 * 60 * 60 * 1000;
/** Inaktivitäts-Fenster mit „Angemeldet bleiben" – 30 Tage. */
export const LONG_INACTIVITY_MS = 30 * 24 * 60 * 60 * 1000;

/** Aktivitäts-Zeitstempel auf „jetzt" setzen (Multi-Tab über localStorage). */
export function touchActivity(): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
}

export function markSessionPolicy(rememberMe: boolean): void {
  if (typeof window === "undefined") return;
  if (rememberMe) {
    window.localStorage.setItem(REMEMBER_KEY, "1");
  } else {
    window.localStorage.removeItem(REMEMBER_KEY);
  }
  window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
  touchActivity();
}

export function clearSessionPolicy(): void {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(REMEMBER_KEY);
  window.localStorage.removeItem(LAST_ACTIVITY_KEY);
  window.sessionStorage.removeItem(LEGACY_SESSION_KEY);
}

/**
 * true, wenn die Session nach unserer Policy abgelaufen ist → der Guard
 * ruft dann signOut() + Redirect auf.
 *
 * Fehlt der Aktivitäts-Marker (frisch nach diesem Update, oder manuell
 * gelöscht), behandeln wir die Session als frisch und setzen den Marker –
 * das verhindert ungewollten Logout beim Deploy oder in einem neuen Tab.
 */
export function isSessionExpiredByPolicy(): boolean {
  if (typeof window === "undefined") return false;
  const remembered = window.localStorage.getItem(REMEMBER_KEY) === "1";
  const limit = remembered ? LONG_INACTIVITY_MS : SHORT_INACTIVITY_MS;

  const last = window.localStorage.getItem(LAST_ACTIVITY_KEY);
  if (last === null) {
    touchActivity();
    return false;
  }
  const lastAt = parseInt(last, 10);
  if (!Number.isFinite(lastAt)) {
    touchActivity();
    return false;
  }
  return Date.now() - lastAt > limit;
}
